import { nip19 } from 'nostr-tools';

/**
 * The npub of the account whose posts are showcased on this website.
 *
 * Change this to any npub / nprofile to showcase a different account.
 */
export const SHOWCASE_NPUB =
  'npub1vw5xewlrduxm94hj2d7c2qxk5zc268ac70420w76cvnjue280l9sam04ks';

/**
 * Event IDs (hex) to hide from the website feed. Add an id here to exclude a
 * specific post from the showcase.
 */
export const HIDDEN_POST_IDS = new Set<string>([]);

/**
 * Case-insensitive text snippets. Any post whose content includes one of these
 * phrases is hidden from the feed. Useful when the event id isn't known.
 */
export const HIDDEN_POST_PHRASES: string[] = [
  'we still have 39,000 sats',
];

export interface NavLink {
  label: string;
  href: string;
  /** Whether the link points off-site (opens in a new tab). */
  external?: boolean;
  /** Visual style. 'button' renders a filled pill call-to-action. */
  variant?: 'link' | 'button';
}

export interface SocialLink {
  label: string;
  href: string;
}

/**
 * Navigation links shown in the header menu.
 * "Home" points to the site root; the rest are external calls-to-action.
 */
export const NAV_LINKS: NavLink[] = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/profile.php?id=61582987418213',
    external: true,
  },
  {
    label: 'Donate',
    href: 'https://pay.zaprite.com/pl_KuWq419zJk',
    external: true,
  },
  {
    label: 'Subscribe',
    href: 'https://mailchi.mp/b894cf7a42d1/vanuatu-bible-translation-desk-newsletter-subscription',
    external: true,
  },
  {
    label: 'Glyphlings',
    href: 'https://drive.google.com/file/d/1QcdlKPHP2vb4eCNj6OZwnXJw11W4lNjD/view?usp=sharing',
    external: true,
    variant: 'button',
  },
];

/** Social links shown in the footer. */
export const SOCIAL_LINKS: SocialLink[] = [
  { label: 'Instagram', href: 'https://www.instagram.com/vbtdesk/' },
  { label: 'YouTube', href: 'https://www.youtube.com/@VBTDesk' },
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/profile.php?id=61582987418213',
  },
  { label: 'X', href: 'https://x.com/VBTDesk' },
];

/** About-page vision & mission image. */
export const ABOUT_IMAGE =
  'https://blossom.dreamith.to/452b4f89a2cfd289a788ad91f13112af9f7b60ad2437210d01c4421df45a38bc.jpeg';

/** Short description used for the About page's SEO/meta tags. */
export const ABOUT_INTRO: string[] = [
  'All languages in Vanuatu, even endangered ones, having quality translations of God\u2019s Word in their heart language \u2014 coordinating, supporting, and strengthening Bible translation across all six provinces.',
];

export interface StaffMember {
  name: string;
  /** Job title or role within the organisation. */
  role: string;
  /** Short biography. */
  bio: string;
  /** Optional photo URL. */
  photo?: string;
}

/**
 * Staff bios shown on the About page.
 *
 * NOTE: These are placeholder entries. Replace the names, roles, bios, and
 * photo URLs with the real team details.
 */
export const STAFF: StaffMember[] = [
  {
    name: 'Pastor Daniel Boe',
    role: 'Coordinator',
    bio: 'Pastor Daniel comes from Malo Island, Sanma Province. He is a husband and a father to two beautiful kids. He helped found the VBT Desk and has been the leader since its inception.',
    photo:
      'https://blossom.dreamith.to/8f505f3220bef51e9d7279a5ac90cbed3009dfac40de0703fa9e50862c55f19f.jpeg',
  },
  {
    name: 'Hendrix Vutilolo',
    role: 'Administrator',
    bio: 'Hendrix is from Tangoa Island, Sanma Province. He is a graduate of Talua Theological Training Institute. He makes sure the VBT Desk runs smoothly.',
    photo:
      'https://blossom.dreamith.to/5013e8bbb81301006e543fbc27e26a117592b8c4a1ea5685ff741090f9134323.jpeg',
  },
  {
    name: 'Ricky DiMartino',
    role: 'Technical Advisor',
    bio: 'Ricky is from the United States, but he is very glad to call Vanuatu home. He lives here with his wife and five kids. He manages the website and gives technical advice to the team.',
    photo:
      'https://blossom.ditto.pub/c092118807cc4702a41142141348b8d79a853d1925c07c875e5ae2d57a39ed0c.jpg',
  },
];

/** Decode the showcase npub into a hex pubkey (throws if malformed). */
export function getShowcasePubkey(): string {
  const decoded = nip19.decode(SHOWCASE_NPUB);
  if (decoded.type === 'npub') {
    return decoded.data;
  }
  if (decoded.type === 'nprofile') {
    return decoded.data.pubkey;
  }
  throw new Error('SHOWCASE_NPUB must be an npub or nprofile');
}
